import { Link, useFetcher, useLoaderData } from "@remix-run/react";
import {
  ChevronDown,
  Loader2,
  MessageCircle,
  Minus,
  Plus,
  Send,
  ThumbsDown,
  ThumbsUp,
  User2,
  Zap,
  Trash2,
  X,
} from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "#app/components/ui/avatar.tsx";
import { Badge } from "#app/components/ui/badge.tsx";
import { Button } from "#app/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "#app/components/ui/dialog.tsx";
import { Input } from "#app/components/ui/input.tsx";
import { Textarea } from "#app/components/ui/textarea.tsx";
import { UserProfileDialog } from "#app/components/user/user-profile-dialog.tsx";
import { type loader as stationLoader } from "#app/routes/estacoes.$stationId.tsx";
import { cn, getUserImgSrc } from "#app/utils/misc.tsx";
import { ThreadMessageForm } from "../threads/thread-message-form.tsx";
import { ThreadMessages } from "../threads/thread-messages.tsx";
import { ThreadReplyCount } from "../threads/thread-reply-count.tsx";

// Custom styles - Add pulse animation for mobile attention
const pulseAnimation = `
  @keyframes pulse-subtle {
    0% { opacity: 0.1; }
    50% { opacity: 0.3; }
    100% { opacity: 0.1; }
  }
`;

// Inject the styles once when the component is imported
if (typeof document !== "undefined") {
  const styleEl = document.createElement("style");
  styleEl.textContent = pulseAnimation;
  document.head.appendChild(styleEl);
}

// Define the Review type (consider moving this to a shared types file)
type Review = {
  id: number;
  comment: string | null;
  rating: number | null;
  createdAt: string | Date;
  kilowatts: number | null;
  vehicleName: string | null;
  vehicleMake: string | null;
  threadMessages?: Array<{
    id: string;
    message: string;
    createdAt: string;
    reviewId: number;
    user: {
      username: string | null;
      image: {
        id: string;
      } | null;
    } | null;
  }>;
  threadMessageCount?: number;
  user: {
    id: string;
    username: string | null;
    score: number | null;
    image: {
      id: string;
    } | null;
  } | null;
};

// Define the props for ReviewsSection
interface ReviewsSectionProps {
  stationId: number;
  stationUrl: string;
  initialReviews: Review[];
  totalReviews: number;
  user: any; // Replace with a specific user type
  reviewFetcher: any; // Type this appropriately based on useFetcher
  formRef: React.RefObject<HTMLFormElement>;
}

// Define the LoaderData type for reviewsFetcher
type ReviewsLoaderData = {
  station: {
    reviews: Review[];
  } | null;
  // Add other properties from the loader if needed
};

const INITIAL_REVIEWS_DISPLAY_COUNT = 3;

export function ReviewsSection({
  stationId,
  stationUrl,
  initialReviews,
  totalReviews,
  user,
  reviewFetcher,
  formRef,
}: ReviewsSectionProps) {
  const [rating, setRating] = React.useState<number | undefined>(undefined);
  const reviewsFetcher = useFetcher<ReviewsLoaderData>();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [reviews, setReviews] = React.useState<Review[]>(initialReviews);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showAllReviews, setShowAllReviews] = React.useState(false); // State for expanded view
  const deleteReviewFetcher = useFetcher();
  const [reviewToDelete, setReviewToDelete] = React.useState<number | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const processedDeleteIdRef = React.useRef<number | null>(null);

  // Function to handle loading more reviews manually
  const handleLoadMore = React.useCallback(() => {
    if (
      !isLoading &&
      reviews.length < totalReviews &&
      stationId &&
      stationUrl
    ) {
      setIsLoading(true);
      const nextPage = currentPage + 1;
      // Use stationUrl directly if it includes the base path, otherwise construct it
      const loadUrl = stationUrl.includes("/estacoes/")
        ? `${stationUrl}?page=${nextPage}`
        : `/estacoes/${stationUrl}${stationId}?page=${nextPage}`;
      reviewsFetcher.load(loadUrl);
      setCurrentPage(nextPage);
    }
  }, [
    isLoading,
    reviews.length,
    totalReviews,
    stationId,
    stationUrl,
    currentPage,
    reviewsFetcher,
  ]);

  // Append new reviews loaded by the fetcher
  React.useEffect(() => {
    if (reviewsFetcher.data && reviewsFetcher.state === "idle") {
      const newReviewsData =
        (reviewsFetcher.data as ReviewsLoaderData).station?.reviews || [];

      // Move filtering logic inside setReviews updater using prevReviews
      setReviews((prevReviews) => {
        const uniqueNewReviews = newReviewsData.filter(
          (nr) => !prevReviews.some((r) => r.id === nr.id),
        );
        // Only create a new array if there are actually new reviews to add
        if (uniqueNewReviews.length > 0) {
          return [...prevReviews, ...uniqueNewReviews];
        }
        // Otherwise, return the existing state reference to prevent re-triggering
        return prevReviews;
      });

      setIsLoading(false);
    } else if (reviewsFetcher.state === "idle") {
      // Ensure loading is false if fetcher goes idle without new data
      setIsLoading(false);
    }
    // Remove 'reviews' from the dependency array to break the loop
  }, [reviewsFetcher.data, reviewsFetcher.state]);

  // Prepend newly submitted review
  React.useEffect(() => {
    const submittedData = reviewFetcher.data as any; // Type this correctly
    if (submittedData?.status === 200 && submittedData.newReview && user) {
      // Format the date before adding to state
      const rawReview = submittedData.newReview.reviews[0];
      const formattedCreatedAt = new Date(
        rawReview.createdAt,
      ).toLocaleDateString("pt-BR", { hour: "2-digit", minute: "2-digit" });

      const newReviewToAdd = {
        ...rawReview,
        createdAt: formattedCreatedAt, // Use the formatted date
        // Ensure the structure matches the Review type
        threadMessages: [],
        threadMessageCount: 0,
      };

      // Check if the review already exists to prevent duplicates
      setReviews((prevReviews) => {
        const reviewExists = prevReviews.some(
          (r) => r.id === newReviewToAdd.id,
        );
        if (!reviewExists) {
          return [newReviewToAdd, ...prevReviews];
        }
        return prevReviews;
      });

      // Reset internal rating state after successful submission
      setRating(undefined);
      // Form reset is handled via formRef in the parent component's useEffect

      // Scroll the page to the reviews heading
      const reviewsHeading = document.getElementById("reviews-heading");
      if (reviewsHeading) {
        reviewsHeading.scrollIntoView({ behavior: "smooth", block: "start" });

        // Ensure the expanded view is active if user submits a review
        if (
          !showAllReviews &&
          totalReviews + 1 > INITIAL_REVIEWS_DISPLAY_COUNT
        ) {
          setShowAllReviews(true);
        }

        // Scroll the container to the top after potentially expanding
        requestAnimationFrame(() => {
          const reviewsContainer = document.getElementById("reviewsContainer");
          if (reviewsContainer) {
            reviewsContainer.scrollTo({ top: 0, behavior: "smooth" });
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFetcher.data, user]); // Keep dependencies minimal if possible, added showAllReviews to check

  // Handle review deletion result
  React.useEffect(() => {
    if (deleteReviewFetcher.data && deleteReviewFetcher.state === "idle") {
      const response = deleteReviewFetcher.data as any;
      if (
        response?.status === 200 &&
        reviewToDelete &&
        reviewToDelete !== processedDeleteIdRef.current
      ) {
        // Remove the deleted review from state
        setReviews((prev) =>
          prev.filter((review) => review.id !== reviewToDelete),
        );
        // Recalculate if the "Show All" button should be visible after deletion
        if (reviews.length - 1 <= INITIAL_REVIEWS_DISPLAY_COUNT) {
          // Might hide the scroll container if count drops below threshold after delete
          // Consider if setShowAllReviews(false) is desired here, or keep it expanded?
          // Let's keep it expanded for now if it was already expanded.
        }

        // Show success toast
        toast.success("Avaliação excluída com sucesso", {
          description: "⚠️ Você perdeu 2 pontos.",
        });

        processedDeleteIdRef.current = reviewToDelete;
        setReviewToDelete(null); // Reset reviewToDelete after processing
      } else if (response?.message && response?.status !== 200) {
        // Show error toast with message from server only on error status
        toast.error("Erro ao excluir avaliação", {
          description: response.message,
        });
        // Optionally reset ref on error if needed, though resetting reviewToDelete might suffice
        // processedDeleteIdRef.current = null;
        setReviewToDelete(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteReviewFetcher.data, deleteReviewFetcher.state, reviewToDelete]); // Added reviews.length to potentially adjust view

  // Determine if the container should be scrollable (only when expanded)
  const isScrollableContainer =
    showAllReviews && reviews.length > INITIAL_REVIEWS_DISPLAY_COUNT;

  return (
    <div className="w-full rounded-lg border border-border bg-card p-4 shadow-md dark:bg-card">
      <h2
        id="reviews-heading"
        className="mb-4 text-xl font-semibold text-card-foreground"
      >
        Últimos Check-ins
      </h2>
      <div className="relative">
        {/* Review List Container: Conditional scrolling and max-height */}
        <div
          className={cn(
            "mb-4 pr-2 pb-2",
            "overflow-hidden transition-all duration-500 ease-in-out",
            isScrollableContainer ? "overflow-y-auto" : "",
          )}
          id="reviewsContainer"
          style={{ maxHeight: isScrollableContainer ? "600px" : undefined }}
        >
          {reviews && reviews.length > 0 ? (
            <>
              {reviews.map((review, index) => (
                <div
                  key={review.id}
                  className={cn(
                    "flex flex-col gap-3 rounded-lg border border-border bg-background shadow-md dark:bg-muted/30",
                    "overflow-hidden transition-all duration-500 ease-in-out",
                    !showAllReviews && index >= INITIAL_REVIEWS_DISPLAY_COUNT
                      ? "max-h-0 opacity-0 p-0 border-0 my-0"
                      : "max-h-[500px] opacity-100 p-4 my-2",
                  )}
                >
                  {/* Row 1: Avatar/Score/Username + Rating/Timestamp */}
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Avatar + Score + Username */}
                    <div className="flex items-start gap-3 flex-shrink-0">
                      {" "}
                      {/* Use items-start */}
                      {/* Wrap Avatar/Username in Dialog Trigger */}
                      {review.user ? (
                        <UserProfileDialog
                          userId={review.user.id}
                          initialUsername={review.user.username}
                          initialImageId={review.user.image?.id}
                          trigger={
                            <div className="flex items-start gap-3 cursor-pointer group">
                              {/* Avatar */}
                              <Avatar className="h-12 w-12 rounded-lg flex-shrink-0 group-hover:opacity-80 transition-opacity">
                                {review.user.image?.id ? (
                                  <AvatarImage
                                    src={getUserImgSrc(review.user.image.id)}
                                    alt={
                                      review.user.username ??
                                      "Avatar do usuário"
                                    }
                                    className="object-cover"
                                    width="48"
                                    height="48"
                                  />
                                ) : (
                                  <AvatarFallback className="rounded-lg bg-muted">
                                    <User2 className="h-6 w-6 text-muted-foreground" />
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              {/* Username & Score & Delete Button */}
                              <div className="flex flex-col items-start">
                                {/* Username + Delete Button */}
                                <div className="flex items-center gap-1.5">
                                  {" "}
                                  {/* Wrap username and delete button */}
                                  <div className="text-sm font-semibold text-foreground group-hover:underline">
                                    {review.user.username || "Usuário"}
                                  </div>
                                  {/* Delete Button (conditionally rendered) */}
                                  {user && user.id === review.user?.id && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-5 w-5 text-destructive/60 hover:text-destructive hover:bg-destructive/10 rounded-md transition-opacity" // Always visible
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent dialog from opening
                                        setReviewToDelete(review.id);
                                        setDeleteDialogOpen(true);
                                      }}
                                      aria-label="Excluir avaliação"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                                {/* Score (Optional) */}
                                {review.user.score != null &&
                                  review.user.score > 0 && (
                                    <div className="mt-0.5 flex items-center rounded-full bg-yellow-100 px-1.5 py-0.5 text-xs text-yellow-700 transition-colors hover:bg-yellow-200 dark:bg-yellow-800/50 dark:text-yellow-300 dark:hover:bg-yellow-800/70">
                                      <Zap
                                        fill="currentColor"
                                        className="mr-0.5 h-3 w-3 text-yellow-500 dark:text-yellow-400"
                                      />
                                      {review.user.score}
                                    </div>
                                  )}
                              </div>
                            </div>
                          }
                        />
                      ) : (
                        // Fallback for anonymous user (no dialog)
                        <div className="flex items-start gap-3">
                          <Avatar className="h-12 w-12 rounded-lg flex-shrink-0">
                            <AvatarFallback className="rounded-lg bg-muted">
                              <User2 className="h-6 w-6 text-muted-foreground" />
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col items-start">
                            <div className="text-sm font-semibold text-muted-foreground">
                              Usuário
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right: Rating + Timestamp (unchanged structure) */}
                    <div className="flex flex-col items-end text-right flex-shrink-0">
                      <div className="mb-1">
                        {review.rating === 1 ? (
                          <Badge
                            variant="outline"
                            className="border-green-500 bg-green-50 text-green-700 dark:border-green-600 dark:bg-green-900/30 dark:text-green-300"
                          >
                            <ThumbsUp className="mr-1 h-3.5 w-3.5 flex-shrink-0" />{" "}
                            OK
                          </Badge>
                        ) : review.rating === 0 ? ( // Corrected: 0 for Falhou
                          <Badge
                            variant="outline"
                            className="border-red-500 bg-red-50 text-red-700 dark:border-red-600 dark:bg-red-900/30 dark:text-red-300"
                          >
                            <ThumbsDown className="mr-1 h-3.5 w-3.5 flex-shrink-0" />{" "}
                            Falhou
                          </Badge>
                        ) : (
                          // Assuming rating === 2 is Neutro (or handle other cases if needed)
                          <Badge
                            variant="outline"
                            className="border-yellow-500 bg-yellow-50 text-yellow-700 dark:border-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-300"
                          >
                            <Minus className="mr-1 h-3.5 w-3.5 flex-shrink-0" />{" "}
                            Neutro
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {typeof review.createdAt === "string"
                          ? review.createdAt
                          : new Date(review.createdAt).toLocaleDateString(
                              "pt-BR",
                              { hour: "2-digit", minute: "2-digit" },
                            )}
                      </p>
                    </div>
                  </div>

                  {/* Row 3: Comment */}
                  <div className="mt-1">
                    {review.comment ? (
                      <p className="max-w-full text-sm text-foreground break-words">
                        {review.comment}
                      </p>
                    ) : (
                      <p className="max-w-full text-sm text-muted-foreground/85 italic">
                        (Usuário não deixou um comentário)
                      </p>
                    )}
                  </div>

                  {/* Row 4: Footer (kW/Vehicle + Actions) */}
                  <div className="mt-2 flex items-center justify-between gap-4 border-t border-border/50 pt-3">
                    {/* Left: kW + Vehicle/Link */}
                    <div className="flex items-center flex-wrap gap-2 min-w-0">
                      {/* Always show kW if present */}
                      {review.kilowatts != null && ( // Check explicitly against null/undefined
                        <Badge variant="secondary" className="text-xs">
                          ⚡ {review.kilowatts} kW
                        </Badge>
                      )}

                      {/* Show Vehicle Name OR "Add Vehicle" link */}
                      {review.vehicleName ? (
                        review.vehicleMake ? (
                          <Link
                            to={`/carros?marca=${encodeURIComponent(review.vehicleMake.toLowerCase())}`}
                            prefetch="intent"
                            className="group flex items-center justify-center"
                          >
                            <Badge
                              variant="secondary"
                              className={cn(
                                "text-xs text-center truncate inline-block group-hover:underline group-hover:bg-accent",
                                review.kilowatts != null // Check explicitly against null/undefined
                                  ? "max-w-[125px] md:max-w-[200px]"
                                  : "max-w-[200px] md:max-w-[250px]",
                              )}
                              title={review.vehicleName}
                            >
                              {review.vehicleName}
                            </Badge>
                          </Link>
                        ) : (
                          <Badge
                            variant="secondary"
                            className={cn(
                              "text-xs text-center truncate inline-block",
                              review.kilowatts != null // Check explicitly against null/undefined
                                ? "max-w-[125px] md:max-w-[200px]"
                                : "max-w-[200px] md:max-w-[250px]",
                            )}
                            title={review.vehicleName}
                          >
                            {review.vehicleName}
                          </Badge>
                        )
                      ) : user &&
                        user.id === review.user?.id &&
                        !user.userCar ? ( // Check user exists
                        <Link
                          to="/garagem"
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <Plus className="h-3 w-3" />
                          Adicionar veículo
                        </Link>
                      ) : (
                        // Optional: Render empty span if neither kW nor vehicle/link exists
                        // If kW exists but vehicle/link doesn't, this won't render, which is fine.
                        review.kilowatts == null && (
                          <span className="text-xs text-muted-foreground"></span>
                        ) // Check explicitly against null/undefined
                      )}
                    </div>

                    {/* Right: Actions (Thread and Delete) */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Thread Dialog Trigger */}
                      <Dialog>
                        <DialogTrigger asChild>
                          <button className="focus:outline-none">
                            <ThreadReplyCount
                              reviewId={review.id}
                              review={review}
                            />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[80vh] max-w-2xl flex flex-col">
                          <DialogHeader className="pb-3 border-b">
                            <DialogTitle className="flex items-center gap-2 text-lg font-medium">
                              <MessageCircle className="h-5 w-5 text-blue-500" />
                              <span>Conversa</span>
                              <Badge
                                variant="outline"
                                className="ml-1 font-normal"
                              >
                                {review.user?.username || "Usuário"}
                              </Badge>
                            </DialogTitle>
                            <DialogDescription>
                              Conversa iniciada por{" "}
                              {review.user?.username || "Usuário"} sobre o
                              check-in.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="flex-1 overflow-y-auto py-4 px-1 -mx-1">
                            <div className="mb-6 rounded-lg bg-blue-50/50 p-4 shadow-sm dark:bg-blue-900/10">
                              <div className="flex gap-3">
                                <div className="flex-shrink-0">
                                  {review.user?.image?.id ? (
                                    <img
                                      src={`https://carregados.com.br/cdn-cgi/image/format=auto,width=40,height=40,fit=cover${getUserImgSrc(
                                        review.user.image.id,
                                      )}`}
                                      alt="user avatar"
                                      className="h-10 w-10 rounded-full object-cover shadow-sm"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground shadow-sm">
                                      <User2 className="h-5 w-5" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-sm font-semibold">
                                      {review.user?.username || "Usuário"}
                                    </p>
                                    <div className="flex items-center">
                                      {review.rating === 1 ? (
                                        <ThumbsUp className="mr-1 h-3 w-3 text-green-500" />
                                      ) : review.rating === 0 ? ( // Corrected: 0 for Falhou
                                        <ThumbsDown className="mr-1 h-3 w-3 text-red-500" />
                                      ) : (
                                        // Assuming rating === 2 is Neutro
                                        <Minus className="mr-1 h-3 w-3 text-yellow-500" />
                                      )}
                                      <p className="text-xs text-muted-foreground">
                                        {typeof review.createdAt === "string"
                                          ? review.createdAt
                                          : new Date(
                                              review.createdAt,
                                            ).toLocaleDateString("pt-BR", {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                      </p>
                                    </div>
                                  </div>
                                  <p className="text-sm leading-relaxed break-words">
                                    {review.comment}
                                  </p>
                                  {(review.kilowatts != null ||
                                    review.vehicleName) && ( // Check explicitly against null/undefined
                                    <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                                      {review.kilowatts != null && ( // Check explicitly against null/undefined
                                        <Badge
                                          variant="secondary"
                                          className="text-xs"
                                        >
                                          ⚡ {review.kilowatts} kW
                                        </Badge>
                                      )}
                                      {review.vehicleName &&
                                        (review.vehicleMake ? (
                                          <Link
                                            to={`/carros?marca=${encodeURIComponent(review.vehicleMake.toLowerCase())}`}
                                            prefetch="intent"
                                            className="group"
                                          >
                                            <Badge
                                              variant="secondary"
                                              className="text-xs text-center group-hover:underline group-hover:bg-accent"
                                            >
                                              {review.vehicleName}
                                            </Badge>
                                          </Link>
                                        ) : (
                                          <Badge
                                            variant="secondary"
                                            className="text-xs text-center"
                                          >
                                            {review.vehicleName}
                                          </Badge>
                                        ))}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="pl-2">
                              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100 dark:border-gray-800">
                                <MessageCircle className="h-4 w-4 text-blue-500" />
                                <h4 className="text-sm font-medium">
                                  Respostas
                                </h4>
                              </div>
                              <ThreadMessages
                                reviewId={review.id}
                                review={review}
                              />
                            </div>
                          </div>
                          <div className="border-t pt-4">
                            <ThreadMessageForm
                              reviewId={review.id}
                              user={user}
                            />
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </div>
              ))}

              {/* "Load More" Button (inside expanded container) */}
              {showAllReviews && reviews.length < totalReviews && (
                <div className="flex justify-center py-4">
                  {isLoading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleLoadMore}
                      disabled={isLoading || reviewsFetcher.state !== "idle"} // Disable while loading or fetching
                    >
                      Carregar mais check-ins
                    </Button>
                  )}
                </div>
              )}
              {/* Message indicating end of reviews */}
              {showAllReviews &&
                reviews.length === totalReviews &&
                totalReviews > INITIAL_REVIEWS_DISPLAY_COUNT && (
                  <p className="text-center text-sm text-muted-foreground py-4">
                    Fim dos check-ins.
                  </p>
                )}
            </>
          ) : (
            <div className="flex h-[280px] flex-col items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center dark:border-gray-600 dark:bg-gray-700/50">
              <MessageCircle
                className="h-16 w-16 text-gray-400 dark:text-gray-500"
                strokeWidth={1}
              />
              <p className="mt-4 text-lg font-medium text-gray-700 dark:text-gray-300">
                Nenhum check-in ainda
              </p>
              <p className="mt-1 flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                Seja o primeiro e ganhe +2 pontos!{" "}
                <Zap
                  fill="currentColor"
                  className="h-4 w-4 text-yellow-500 flex-shrink-0"
                />
              </p>
            </div>
          )}
        </div>

        {/* Gradient Fade: Only show when scrollable */}
        {isScrollableContainer &&
          reviews.length > INITIAL_REVIEWS_DISPLAY_COUNT && (
            <div
              className={cn(
                "absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-card to-transparent dark:from-card",
                "pointer-events-none",
              )}
            />
          )}
      </div>

      {/* "Show All" Button: Only show if not expanded and there are more reviews than initially shown */}
      {!showAllReviews && totalReviews > INITIAL_REVIEWS_DISPLAY_COUNT && (
        <div className="mb-4 flex justify-center">
          <Button
            variant="secondary"
            className="text-primary/90 hover:text-primary hover:bg-accent/80"
            onClick={() => {
              setShowAllReviews(true);
              // Scroll to the top of the reviews section after expanding
              const reviewsHeading = document.getElementById("reviews-heading");
              if (reviewsHeading) {
                reviewsHeading.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }}
          >
            Ver todos os {totalReviews} check-ins
            <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
        </div>
      )}

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) {
            setReviewToDelete(null);
            processedDeleteIdRef.current = null;
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir check-in</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir este check-in? Você perderá 2
              pontos e esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row justify-end gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setReviewToDelete(null);
                processedDeleteIdRef.current = null;
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (reviewToDelete) {
                  deleteReviewFetcher.submit(
                    {
                      intent: "delete-review",
                      reviewId: reviewToDelete.toString(),
                      userId: user?.id || "",
                      locationId: stationId.toString(),
                    },
                    { method: "post" },
                  );
                  setDeleteDialogOpen(false);
                }
              }}
              disabled={deleteReviewFetcher.state === "submitting"}
            >
              {deleteReviewFetcher.state === "submitting" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-6 border-t border-border pt-6">
        {user ? (
          <reviewFetcher.Form
            ref={formRef}
            encType="multipart/form-data"
            method="post"
            className="space-y-5"
          >
            <h3 className="text-lg font-medium text-foreground">
              Deixe seu check-in
            </h3>
            <Input type="hidden" name="locationId" value={stationId} />
            <Input type="hidden" name="userId" value={user.id} />
            <Input type="hidden" name="intent" value="review" />
            <Input
              type="hidden"
              name="rating"
              value={rating?.toString() ?? ""}
            />

            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">
                Você conseguiu carregar?
              </label>
              <div className="flex space-x-3">
                {[
                  {
                    value: 1,
                    label: "Sim, carreguei!",
                    icon: ThumbsUp,
                    color: "green",
                  },
                  {
                    value: 2,
                    label: "Neutro / Outro",
                    icon: Minus,
                    color: "yellow",
                  },
                  {
                    value: 0,
                    label: "Não, falhou!",
                    icon: ThumbsDown,
                    color: "red",
                  },
                ].map(({ value, label, icon: Icon, color }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRating(value)}
                    className={cn(
                      "flex flex-col items-center justify-center rounded-lg border p-3 text-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2",
                      "w-1/3 h-20 relative group/rating",
                      {
                        // Green (Success)
                        "border-green-500 bg-green-50 ring-green-500 dark:bg-green-900/30 dark:border-green-600":
                          rating === value && color === "green",
                        "border-border hover:border-green-300 hover:bg-green-50/50 hover:shadow-sm dark:hover:bg-green-900/30 dark:hover:border-green-800":
                          color === "green" && rating !== value,

                        // Yellow (Neutral)
                        "border-yellow-500 bg-yellow-50 ring-yellow-500 dark:bg-yellow-900/30 dark:border-yellow-600":
                          rating === value && color === "yellow",
                        "border-border hover:border-yellow-300 hover:bg-yellow-50/50 hover:shadow-sm dark:hover:bg-yellow-900/30 dark:hover:border-yellow-800":
                          color === "yellow" && rating !== value,

                        // Red (Failed)
                        "border-red-500 bg-red-50 ring-red-500 dark:bg-red-900/30 dark:border-red-600":
                          rating === value && color === "red",
                        "border-border hover:border-red-300 hover:bg-red-50/50 hover:shadow-sm dark:hover:bg-red-900/30 dark:hover:border-red-800":
                          color === "red" && rating !== value,

                        // Default states
                        "bg-muted/20 dark:bg-muted/10":
                          rating === undefined || rating === null,
                        "bg-background dark:bg-background":
                          rating !== undefined &&
                          rating !== null &&
                          rating !== value,
                      },
                    )}
                    aria-pressed={rating === value}
                    aria-label={label}
                  >
                    <Icon
                      className={cn(
                        "h-6 w-6 mb-1 transition-colors duration-200 flex-shrink-0",
                        rating === value
                          ? `text-${color}-600 dark:text-${color}-400`
                          : `text-muted-foreground group-hover/rating:text-${color}-500/70 dark:group-hover/rating:text-${color}-400/70`, // Use group-hover/rating
                      )}
                    />
                    <span
                      className={cn(
                        "text-xs font-medium transition-colors duration-200",
                        rating === value
                          ? `text-${color}-700 dark:text-${color}-300`
                          : `text-foreground/80 group-hover/rating:text-${color}-600/90 dark:group-hover/rating:text-${color}-400/90`, // Use group-hover/rating
                      )}
                    >
                      {label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label
                htmlFor="feedback"
                className="mb-1 block text-sm font-medium text-foreground"
              >
                Conte sua experiência (opcional)
              </label>
              <Textarea
                id="feedback"
                name="comment"
                rows={3}
                placeholder="Ex: Carregou rápido, mas achei caro."
                maxLength={600}
                autoComplete="off"
                className="mt-1 block w-full rounded-md border-border bg-input/30 p-2 shadow-sm focus:border-primary focus:ring focus:ring-primary/20 dark:bg-input/80"
              />
            </div>

            {/* Kilowatts Input (Optional Section) */}
            <div className="grid grid-cols-1 gap-4">
              {" "}
              {/* Changed back to grid-cols-1 */}
              <div>
                <label
                  htmlFor="kilowatts"
                  className="mb-1 block text-sm font-medium text-foreground"
                >
                  Potência (kW) (opcional)
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Zap className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <Input
                    id="kilowatts"
                    name="kilowatts"
                    type="number"
                    step="0.1"
                    min="0"
                    className="block w-full rounded-md border-border bg-input/30 pl-10 pr-12 focus:border-primary focus:ring focus:ring-primary/20 dark:bg-input/80"
                    placeholder="Ex: 22"
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-sm text-muted-foreground">kW</span>
                  </div>
                </div>
              </div>
            </div>

            {reviewFetcher.data && reviewFetcher.data.message && (
              <p className="text-sm text-destructive">
                {reviewFetcher.data.message}
              </p>
            )}

            <Button
              type="submit"
              className="w-full flex items-center justify-center gap-2"
              size="lg"
              disabled={
                reviewFetcher.state === "submitting" || rating === undefined
              }
            >
              {reviewFetcher.state === "submitting" ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
              <span>
                {reviewFetcher.state !== "idle"
                  ? "Enviando Check-in..."
                  : "Enviar Check-in"}
              </span>
            </Button>
          </reviewFetcher.Form>
        ) : (
          <div className="mt-6 rounded-lg border border-primary/20 bg-primary/5 p-6 text-center dark:border-primary/30 dark:bg-primary/10">
            <Zap className="mx-auto h-8 w-8 text-primary mb-3" />
            <h3 className="text-lg font-medium text-primary-foreground dark:text-primary-300">
              Participe da Comunidade!
            </h3>
            <p className="mt-1 text-sm text-primary/80 dark:text-primary/70 mb-4">
              Faça login para deixar seu check-in, ganhar pontos e ajudar outros
              motoristas de elétricos.
            </p>
            <Link
              rel="nofollow"
              to={`/login?redirectTo=/estacoes/${stationUrl}${stationId}`}
            >
              <Button className="w-full sm:w-auto" variant="default" size="lg">
                Entrar ou Criar Conta
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
